"use client";

import React, { createContext, useContext, useState } from 'react';
import { Language, TranslationKeys } from '../../types/i18n';
import { en } from '../../translations/en';
import { no } from '../../translations/no';
import { se } from '../../translations/se';

type I18nProviderProps = {
  children: React.ReactNode;
  defaultLanguage?: Language;
  storageKey?: string;
};

type I18nProviderState = {
  language: Language;
  setLanguage: (language: Language) => void;
  t: TranslationKeys;
};

const translations = {
  en,
  no,
  se,
};

const initialState: I18nProviderState = {
  language: 'en',
  setLanguage: () => null,
  t: en,
};

const I18nProviderContext = createContext<I18nProviderState>(initialState);

export function I18nProvider({
  children,
  defaultLanguage = 'en',
  storageKey = 'nbrp-ui-language',
  ...props
}: I18nProviderProps) {
  const [language, setLanguage] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage?.getItem(storageKey) as Language;
      if (stored && (stored === 'en' || stored === 'no' || stored === 'se')) {
        return stored;
      }
      
      // Try to detect browser language
      const browserLang = navigator.language.toLowerCase();
      if (browserLang.startsWith('no') || browserLang.startsWith('nb') || browserLang.startsWith('nn')) {
        return 'no';
      }
      if (browserLang.startsWith('sv') || browserLang.startsWith('se')) {
        return 'se';
      }
    }
    return defaultLanguage;
  });

  const value = {
    language,
    setLanguage: (newLanguage: Language) => {
      if (typeof window !== 'undefined') {
        localStorage?.setItem(storageKey, newLanguage);
      }
      setLanguage(newLanguage);
    },
    t: translations[language],
  };

  return (
    <I18nProviderContext.Provider {...props} value={value}>
      {children}
    </I18nProviderContext.Provider>
  );
}

export const useI18n = () => {
  const context = useContext(I18nProviderContext);

  if (context === undefined)
    throw new Error('useI18n must be used within an I18nProvider');

  return context;
};

// Helper hook for quick access to translations
export const useTranslations = () => {
  const { t } = useI18n();
  return t;
};
